import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';

import { TasksIcon } from '../../../../assets/svg/TasksIcon';
import { getMarkerStatusColor } from '../../../../constants/Colors';
import { ROUTE_START_LOCATION } from '../../../../constants/Routes';
import { StopStatus } from '../../../../constants/Stops';
import { StopReport } from '../../../../domain/report/StopReport';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { Text } from '../../../../react-components/Text';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { StopReportIconsChip } from './stop/StopChips';

const LabelStop = ({ labelStyles, children }: React.PropsWithChildren<{ labelStyles: React.CSSProperties }>) => {
  return (
    <Text
      style={{
        ...labelStyles,
        margin: `3px 0px`,
        lineHeight: `1.5em`,
        WebkitLineClamp: 2,
        WebkitBoxOrient: `vertical`,
        color: !children ? RoutalPalette.grey7 : labelStyles.color ?? undefined,
        fontStyle: !children ? `italic` : undefined,
      }}
    >
      {children ? children : intl.formatMessage({ id: `stop.label_stop.empty` })}
    </Text>
  );
};

type StopPillInfoProps = {
  label?: string;
  labelSelectable?: boolean;
  subLabel?: string;
  subLabelSelectable?: boolean;
  id?: string;
  stopOrder: number;
  maxStops: number;
  stopStatus?: StopStatus;
  stopReport?: StopReport;
  isSpecificStop?: boolean;
  numberTasks?: number;
  subLabelTime?: string;
};

// StopPill information such as stop order, stop status, stop report icons, etc.
export const StopPillInfo = ({
  label,
  labelSelectable = false,
  subLabel,
  subLabelSelectable = false,
  id,
  stopOrder,
  maxStops,
  stopStatus,
  stopReport,
  isSpecificStop,
  numberTasks,
  subLabelTime,
}: StopPillInfoProps) => {
  const { data: vehicleData } = useRetrieveVehicle();

  return (
    // Main container
    <Grid style={{ display: `flex`, flexDirection: `row`, width: `100%` }}>
      <Grid style={{ display: `flex`, flexDirection: `column`, justifyContent: `center`, width: `100%` }}>
        <VSpacer small />
        <LabelStop
          labelStyles={{
            color: RoutalPalette.text,
            fontWeight: 600,
            fontSize: `16px`,
            userSelect: labelSelectable ? `text` : `none`,
          }}
        >
          {label}
        </LabelStop>

        <Grid style={{ overflow: `hidden`, display: `flex`, flexDirection: `row`, alignItems: `center` }}>
          <div
            style={{
              backgroundColor: RoutalPalette.primary80,
              color: RoutalPalette.white,
              display: `block`,
              float: `left`,
              borderRadius: `5px`,
              fontSize: `10px`,
              padding: `2px 4px`,
              marginRight: `6px`,
            }}
          >
            {isSpecificStop ? (
              <Text
                style={{ color: RoutalPalette.white }}
                intlId={id === ROUTE_START_LOCATION ? `drivers.stop.start_location` : `drivers.stop.end_location`}
              />
            ) : (
              stopOrder + `/` + maxStops
            )}
          </div>

          {subLabel ? (
            <LabelStop
              labelStyles={{
                color: RoutalPalette.secondary00,
                fontSize: `13px`,
                userSelect: subLabelSelectable ? `text` : `none`,
              }}
            >
              {subLabel}
            </LabelStop>
          ) : null}

          {subLabelTime && (
            <>
              <div
                style={{
                  height: `4px`,
                  width: `4px`,
                  backgroundColor: RoutalPalette.secondary00,
                  borderRadius: `50%`,
                  margin: `0px 6px`,
                }}
              />

              <LabelStop
                labelStyles={{
                  color: RoutalPalette.secondary00,
                  fontSize: `13px`,
                  userSelect: subLabelSelectable ? `text` : `none`,
                }}
              >
                {subLabelTime}
              </LabelStop>
            </>
          )}
        </Grid>

        {stopStatus && stopStatus !== `pending` && stopReport && (
          <>
            <VSpacer small />
            <Grid container direction="row" wrap="nowrap">
              {[
                <Text
                  intlId={`stop.details.status.${stopStatus}`}
                  style={{
                    backgroundColor: getMarkerStatusColor(stopStatus, `medium`),
                    color: RoutalPalette.white,
                    borderRadius: `10px`,
                    padding: `5px 12px`,
                  }}
                />,
                <StopReportIconsChip
                  report={stopReport}
                  color={RoutalPalette.secondary40}
                  containerStyle={{
                    height: `100%`,
                  }}
                />,
                ...(vehicleData?.project.tasks_enabled && stopReport.tasks_total > 0
                  ? [
                      <Text
                        intlId="stop.details.tasks_summary"
                        intlValues={{
                          complete: stopReport.tasks_completed,
                          total: stopReport.tasks_total,
                        }}
                        style={{
                          backgroundColor: RoutalPalette.secondary40,
                          color: RoutalPalette.white,
                          borderRadius: `10px`,
                          padding: `5px 8px`,
                        }}
                      />,
                    ]
                  : []),
              ].map((element, index, array) => {
                return (
                  <Grid key={index} style={{ marginRight: index !== array.length - 1 ? `8px` : `0px` }}>
                    {element}
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
        {vehicleData?.project.tasks_enabled &&
          stopStatus &&
          stopStatus === `pending` &&
          numberTasks !== undefined &&
          numberTasks !== 0 && (
            <>
              <VSpacer small />
              <Grid
                container
                direction="row"
                wrap="nowrap"
                style={{
                  backgroundColor: RoutalPalette.neutral10,
                  display: `flex`,
                  borderRadius: `5px`,
                  padding: `4px 8px`,
                  flexWrap: `wrap`,
                  width: `max-content`,
                }}
              >
                <TasksIcon />

                <Text
                  intlId="stop.details.tasks_preview"
                  intlValues={{ total: numberTasks }}
                  style={{ marginLeft: `4px`, color: RoutalPalette.secondary60 }}
                />
              </Grid>
            </>
          )}
      </Grid>
    </Grid>
  );
};
